import React, { useState, useContext, useEffect, useRef } from "react";
import api from "../../utils/Api";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

export default function LoginForm() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors]     = useState({});
  const [generalError, setGeneralError] = useState("");
  const [sessionExpiredMsg] = useState(searchParams.get("expired") === "1");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]   = useState(false);

  // ── Efecto typewriter ────────────────────────────────────────────────────
  const LINES   = ["Hola,", "Turnora!"];
  const SPEED   = 70;   // ms por carácter
  const PAUSE   = 600;  // pausa entre líneas
  const [typed, setTyped]       = useState(["", ""]);
  const [showCursor, setShowCursor] = useState(true);
  const lineRef = useRef(0);
  const charRef = useRef(0);

  useEffect(() => {
    let timeout;
    const type = () => {
      const l = lineRef.current;
      const c = charRef.current;
      if (l >= LINES.length) {
        // Parpadeo del cursor al terminar
        const blink = setInterval(() => setShowCursor(p => !p), 530);
        return () => clearInterval(blink);
      }
      if (c < LINES[l].length) {
        setTyped(prev => {
          const next = [...prev];
          next[l] = LINES[l].slice(0, c + 1);
          return next;
        });
        charRef.current++;
        timeout = setTimeout(type, SPEED);
      } else {
        lineRef.current++;
        charRef.current = 0;
        timeout = setTimeout(type, PAUSE);
      }
    };
    timeout = setTimeout(type, 300);
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = "El email es obligatorio.";
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = "Email no válido.";
    if (!password.trim()) e.password = "La contraseña es obligatoria.";
    else if (password.length < 8) e.password = "Mínimo 8 caracteres.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");
    const formErrors = validate();
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;
    setLoading(true);
    try {
      const response = await api.post("/login", { email, password });
      const { token } = response.data;
      login(token);
      const decoded = jwtDecode(token);
      if (decoded.rol === "admin") navigate("/admin");
      else if (decoded.rol === "empleado") navigate("/empleados");
      else navigate("/");
    } catch (error) {
      if (error.isValidationError) {
        setErrors(error.errors || {});
        if (!Object.keys(error.errors || {}).length) setGeneralError(error.message);
      } else if (error.response?.data) {
        setErrors(error.response.data.errors || {});
        if (!error.response.data.errors)
          setGeneralError(error.response.data.error || "Credenciales incorrectas.");
      } else {
        setGeneralError("Error al iniciar sesión. Inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* ── Panel izquierdo ────────────────────────────────────────── */}
      <div className="tn-auth-left" style={{
        flex: "0 0 48%",
        background: "linear-gradient(145deg, #0F1629 0%, #1A2347 50%, #1E3A8A 100%)",
        display: "flex",
        flexDirection: "column",
        padding: "48px 56px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Patrón diagonal geométrico */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diag" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
              <line x1="0" y1="0" x2="0" y2="120" stroke="white" strokeWidth="1"/>
              <line x1="40" y1="0" x2="40" y2="120" stroke="white" strokeWidth="1"/>
              <line x1="80" y1="0" x2="80" y2="120" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diag)" />
        </svg>

        {/* Glow naranja */}
        <div style={{
          position: "absolute", bottom: "-60px", right: "-60px",
          width: "300px", height: "300px",
          background: "radial-gradient(circle, rgba(255,122,0,0.18) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />

        {/* Icono estrella / asterisco */}
        <div style={{ position: "relative", zIndex: 1, marginBottom: "auto" }}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ marginBottom: "48px" }}>
            <line x1="32" y1="4"  x2="32" y2="60" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="4"  y1="32" x2="60" y2="32" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="11" y1="11" x2="53" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
            <line x1="53" y1="11" x2="11" y2="53" stroke="white" strokeWidth="7" strokeLinecap="round"/>
          </svg>

          <h2 style={{
            fontSize: "clamp(36px, 4vw, 52px)",
            fontWeight: "800",
            color: "#FFFFFF",
            lineHeight: "1.1",
            letterSpacing: "-1.5px",
            margin: "0 0 20px",
          }}>
            {typed[0]}<br />
            <span style={{ color: "#FF7A00" }}>
              {typed[1]}
              <span style={{
                display: "inline-block",
                width: "3px",
                height: "0.85em",
                background: "#FF7A00",
                marginLeft: "3px",
                verticalAlign: "middle",
                borderRadius: "1px",
                opacity: showCursor ? 1 : 0,
                transition: "opacity 0.1s",
              }} />
            </span>
          </h2>

          <p style={{
            fontSize: "16px",
            color: "rgba(255,255,255,0.6)",
            lineHeight: "1.7",
            maxWidth: "320px",
            margin: 0,
          }}>
            Gestiona turnos, fichajes y ausencias de tu equipo de forma automática y sin complicaciones.
          </p>
        </div>

        {/* Footer */}
        <p style={{ position: "relative", zIndex: 1, fontSize: "13px", color: "rgba(255,255,255,0.3)", margin: 0, marginTop: "48px" }}>
          © 2026 Turnora. Todos los derechos reservados.
        </p>
        <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.3)", margin: 0}}>
          Desarrollado por NewTRON Informática
        </p>
      </div>

      {/* ── Panel derecho ─────────────────────────────────────────── */}
      <div style={{
        flex: 1,
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        padding: "40px 56px",
      }}>
        {/* Brand top */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "auto" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF7A00" className="bi bi-clock-history me-2 align-middle" viewBox="0 0 16 16"><path d="M8.515 1.019A7 7 0 1 0 15 8a.5.5 0 0 0-1 0 6 6 0 1 1-5.485-6.981z"></path><path d="M7.5 3a.5.5 0 0 1 .5.5v4.25l3.15 1.85a.5.5 0 1 1-.5.866L7.5 8.5V3.5A.5.5 0 0 1 7.5 3z"></path></svg>
          <span style={{ fontSize: "16px", fontWeight: "700", color: "#0F172A", letterSpacing: "-0.3px" }}>
            Turnora
          </span>
        </div>

        {/* Form centered */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: "380px", width: "100%" }}>

          <div style={{ marginBottom: "32px" }}>
            <h1 style={{ fontSize: "32px", fontWeight: "800", color: "#0F172A", margin: "0 0 10px", letterSpacing: "-1px" }}>
              ¡Bienvenido!
            </h1>
            <p style={{ fontSize: "14px", color: "#64748B", margin: 0 }}>
              ¿No tienes cuenta?{" "}
              <Link to="/register" style={{ color: "#FF7A00", fontWeight: "600", textDecoration: "underline" }}>
                Crea una gratis ahora
              </Link>
              , es muy rápido.
            </p>
          </div>

          {sessionExpiredMsg && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              padding: "12px 14px", marginBottom: "20px",
              background: "#FFF7ED", border: "1px solid #FED7AA",
              borderRadius: "8px"
            }}>
              <AlertCircle size={16} style={{ color: "#EA580C", flexShrink: 0, marginTop: "1px" }} />
              <span style={{ fontSize: "13px", color: "#7C2D12" }}>Tu sesión ha caducado. Por favor, inicia sesión de nuevo.</span>
            </div>
          )}

          {generalError && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              padding: "12px 14px", marginBottom: "20px",
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: "8px"
            }}>
              <AlertCircle size={16} style={{ color: "#EF4444", flexShrink: 0, marginTop: "1px" }} />
              <span style={{ fontSize: "13px", color: "#7F1D1D" }}>{generalError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email — underline style */}
            <div style={{ marginBottom: "28px" }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hola@turnora.es"
                autoComplete="email"
                style={{
                  width: "100%", padding: "10px 4px",
                  background: "transparent",
                  border: "none",
                  borderBottom: `2px solid ${errors.email ? "#EF4444" : "#E2E8F0"}`,
                  borderRadius: 0,
                  fontSize: "15px", color: "#0F172A",
                  outline: "none", fontFamily: "inherit",
                  transition: "border-color 0.15s"
                }}
                onFocus={(e) => { if (!errors.email) e.target.style.borderBottomColor = "#FF7A00"; }}
                onBlur={(e) => { if (!errors.email) e.target.style.borderBottomColor = "#E2E8F0"; }}
              />
              {errors.email && <p style={{ fontSize: "12px", color: "#EF4444", margin: "6px 0 0", fontWeight: "500" }}>{errors.email}</p>}
            </div>

            {/* Password — underline style */}
            <div style={{ marginBottom: "36px" }}>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  autoComplete="current-password"
                  style={{
                    width: "100%", padding: "10px 40px 10px 4px",
                    background: "transparent",
                    border: "none",
                    borderBottom: `2px solid ${errors.password ? "#EF4444" : "#E2E8F0"}`,
                    borderRadius: 0,
                    fontSize: "15px", color: "#0F172A",
                    outline: "none", fontFamily: "inherit",
                    transition: "border-color 0.15s", boxSizing: "border-box"
                  }}
                  onFocus={(e) => { if (!errors.password) e.target.style.borderBottomColor = "#FF7A00"; }}
                  onBlur={(e) => { if (!errors.password) e.target.style.borderBottomColor = "#E2E8F0"; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute", right: "4px", top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", color: "#94A3B8",
                    cursor: "pointer", display: "flex", padding: "4px"
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p style={{ fontSize: "12px", color: "#EF4444", margin: "6px 0 0", fontWeight: "500" }}>{errors.password}</p>}
            </div>

            {/* Botón principal */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", height: "52px",
                background: loading ? "#CBD5E1" : "#0F172A",
                color: "white",
                border: "none", borderRadius: "10px",
                fontSize: "15px", fontWeight: "700",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                fontFamily: "inherit", transition: "background 0.15s",
                marginBottom: "16px",
                letterSpacing: "-0.2px"
              }}
              onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#1E2947")}
              onMouseLeave={(e) => !loading && (e.currentTarget.style.background = "#0F172A")}
            >
              {loading ? (
                <>
                  <span style={{
                    width: "16px", height: "16px",
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTop: "2px solid white",
                    borderRadius: "50%", animation: "spin 0.8s linear infinite"
                  }} />
                  Entrando...
                </>
              ) : "Entrar ahora"}
            </button>

            {/* Olvidé contraseña */}
            <p style={{ textAlign: "center", fontSize: "13px", color: "#64748B", margin: 0 }}>
              ¿Olvidaste tu contraseña?{" "}
              <Link to="/reset-password" style={{ color: "#FF7A00", fontWeight: "700", textDecoration: "none" }}>
                Haz clic aquí
              </Link>
            </p>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </form>
        </div>

        {/* Spacer */}
        <div style={{ marginTop: "auto" }} />
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 860px) {
          .tn-auth-left { display: none !important; }
        }
      `}</style>
    </div>
  );
}